import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Circle, ArrowUpRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { errMsg, fmtCompact, fmtMoney, fmtPct, fmtPeriod, useApi } from "@/lib/api";
import { Donut, Trend } from "@/components/StatementCharts";
import { FamilyBookButton } from "@/components/FamilyBookButton";

const Kpi = ({ label, value, sub, testid, delay }) => (
  <div className="panel p-6 fade-up" style={{ animationDelay: `${delay}ms` }}>
    <div className="small-label">{label}</div>
    <div className="num text-3xl font-semibold text-slate-900 mt-3" data-testid={testid}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
  </div>
);

const Sources = ({ sources }) => (
  <div className="panel p-6 fade-up" style={{ animationDelay: "240ms" }}>
    <div className="flex items-center justify-between">
      <div className="small-label">Uploads this month end</div>
      <Link to="/uploads" className="text-xs text-slate-500 hover:text-slate-900 transition-colors duration-200" data-testid="dash-goto-uploads">Manage</Link>
    </div>
    <ul className="mt-4 space-y-3">
      {sources.map((s) => (
        <li key={s.source} className="flex items-center gap-3 text-sm" data-testid={`dash-source-${s.source}`}>
          {s.uploaded ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <Circle className="h-4 w-4 text-slate-300" />}
          <span className={s.uploaded ? "text-slate-900" : "text-slate-400"}>{s.label}</span>
          {s.filename && <span className="ml-auto font-mono text-[11px] text-slate-400 truncate max-w-[45%]">{s.filename}</span>}
        </li>
      ))}
    </ul>
  </div>
);

const Holders = ({ rows }) => (
  <div className="panel p-6 lg:col-span-2 fade-up" style={{ animationDelay: "300ms" }}>
    <div className="small-label">Top-level holders · net worth</div>
    <table className="w-full mt-4 text-sm">
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-slate-100 last:border-0 group">
            <td className="py-2.5">
              <Link to={`/reports/networth/${r.id}`} className="flex items-center gap-2 text-slate-800 group-hover:text-amber-700 transition-colors duration-200" data-testid={`dash-holder-${r.id}`}>
                {r.name}
                <ArrowUpRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </Link>
            </td>
            <td className="py-2.5 text-xs font-mono uppercase text-slate-400">{r.kind}</td>
            <td className="py-2.5 num text-right text-slate-900">{fmtMoney(r.net_worth, true)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Mix = ({ rows, period }) => {
  const api = useApi();
  const [points, setPoints] = useState([]);
  useEffect(() => {
    api.get("/dashboard/trend", { params: { upto: period } }).then((r) => setPoints(r.data)).catch(() => setPoints([]));
  }, [api, period]);
  return (
    <div className="lg:col-span-4 grid grid-cols-1 lg:grid-cols-2 gap-6 fade-up" style={{ animationDelay: "360ms" }} data-testid="dash-family-charts">
      <Donut rows={rows} title="Family asset mix (look-through)" />
      <Trend points={points} />
    </div>
  );
};

const Warnings = ({ rows }) => (
  <div className="panel p-6 lg:col-span-2 fade-up" style={{ animationDelay: "420ms" }} data-testid="dash-warnings">
    <div className="flex items-center justify-between">
      <div className="small-label">Ownership checks</div>
      <Link to="/ownership" className="text-xs text-slate-500 hover:text-slate-900 transition-colors duration-200" data-testid="dash-goto-ownership">Fix in ownership</Link>
    </div>
    {rows.length === 0 ? (
      <p className="ok-badge mt-4">All ownership percentages are complete</p>
    ) : (
      <ul className="mt-4 space-y-2 max-h-80 overflow-y-auto pr-2">
        {rows.map((w, k) => (
          <li key={k} className="flex gap-2 text-sm text-slate-700">
            <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
            {w.type === "pct_total"
              ? <span><b className="font-medium">{w.name}</b> owners total {fmtPct(w.total)}</span>
              : <span><b className="font-medium">{w.name}</b> holds <b className="font-medium">{w.target_name}</b> with no % set</span>}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const { period, me } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/dashboard", { params: period ? { period } : {} }).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [api, period]);

  if (err) return <p className="text-red-700" data-testid="dash-error">{err}</p>;
  if (!d) return <p className="small-label">Loading…</p>;
  if (d.empty) {
    return (
      <div className="panel p-12 max-w-2xl" data-testid="dash-empty">
        <h1 className="font-serif text-4xl">No month-end data yet</h1>
        <p className="text-slate-600 mt-4">Upload your first spreadsheets to build entity and net worth statements.</p>
        <Link to="/uploads" className="inline-block mt-8 bg-slate-900 text-white px-6 py-3 text-sm hover:bg-slate-800 transition-colors duration-200" data-testid="dash-empty-upload">Upload spreadsheets</Link>
      </div>
    );
  }
  return (
    <div className="max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="small-label">{me?.tenant_name}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2" data-testid="dash-title">
            Month end {fmtPeriod(d.period)}
          </h1>
        </div>
        <FamilyBookButton api={api} period={d.period} tenantId={me?.tenant_id} defaultFor={me?.tenant_name} />
      </div>
      <p className="text-slate-500 mt-3 text-sm">
        Ownership percentages in use: {d.ownership_period ? `set on ${fmtPeriod(d.ownership_period)}` : "none yet"}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
        <Kpi label="Combined net worth" value={fmtCompact(d.total_net_worth)} sub={`${d.top_holder_count} top-level holders`} testid="kpi-net-worth" delay={0} />
        <Kpi label="Entities" value={d.counts.entity} sub="LLCs, partnerships, corporations" testid="kpi-entities" delay={60} />
        <Kpi label="Trusts & estates" value={d.counts.trust} testid="kpi-trusts" delay={120} />
        <Kpi label="Individuals" value={d.counts.individual} sub={`${d.warnings.length} ownership checks open`} testid="kpi-individuals" delay={180} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
        <Holders rows={d.top_holders} />
        <div className="lg:col-span-2 grid gap-6"><Sources sources={d.sources} /></div>
        <Mix rows={d.asset_mix} period={d.period} />
        <Warnings rows={d.warnings} />
      </div>
    </div>
  );
}
